import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "../components/site/header";
import { SiteFooter } from "../components/site/footer";
import { BottomNav } from "../components/site/bottom-nav";
import { Mascot } from "../components/site/mascot";
import { GlobalBreakingMarquee } from "../components/site/global-marquee";
import appIcon from "../assets/icons/app-icon.png.asset.json";
import fontBarlow400 from "@fontsource/barlow/files/barlow-latin-400-normal.woff2?url";
import fontBarlowCondensed700 from "@fontsource/barlow-condensed/files/barlow-condensed-latin-700-normal.woff2?url";
import fontBebasNeue400 from "@fontsource/bebas-neue/files/bebas-neue-latin-400-normal.woff2?url";
import { supabase } from "../integrations/supabase/client";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl text-navy">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          That page is offside. Head back to the home page.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong. Try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "Argentinian.Soccer | Forum & Argentina Football News" },
      {
        name: "description",
        content:
          "Argentinian.Soccer — the home of Argentina football. National Team, Primera División, transfers, polls, and the diaspora abroad.",
      },
      { name: "author", content: "Argentinian.Soccer" },
      { name: "theme-color", content: "#0a1a3d" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Argentinian.Soccer" },
      { name: "mobile-web-app-capable", content: "yes" },
      { property: "og:site_name", content: "Argentinian.Soccer" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Argentinian.Soccer | Forum & Argentina Football News" },
      {
        property: "og:description",
        content: "Argentina's football daily: National Team, Primera División, transfers, and more.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Argentinian.Soccer | Forum & Argentina Football News" },
      { name: "twitter:description", content: "Argentina's football daily: National Team, Primera División, transfers, polls, and more." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/cnxcr9tI9rQW3umVqv7oltlYxyE3/social-images/social-1782546842441-5415.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/cnxcr9tI9rQW3umVqv7oltlYxyE3/social-images/social-1782546842441-5415.webp" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", type: "image/png", href: appIcon.url },
      { rel: "apple-touch-icon", href: appIcon.url },
      // Speed up first image byte by warming the CDN connection.
      { rel: "preconnect", href: "https://storage.googleapis.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://storage.googleapis.com" },
      { rel: "preload", as: "font", type: "font/woff2", href: fontBarlow400, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: fontBarlowCondensed700, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: fontBebasNeue400, crossOrigin: "anonymous" },
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsMediaOrganization",
          name: "Argentinian.Soccer",
          url: "https://argentinian.soccer",
          slogan: "Argentina Football News",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <SiteHeader />
        <GlobalBreakingMarquee />
        <main className="flex-1 pb-28">

          <Outlet />
        </main>
        <SiteFooter />
        <BottomNav />
        <Mascot />
      </div>
    </QueryClientProvider>
  );
}
